import {useEditor} from 'TemplateEditor/EditorProvider'
import {ListBlocks} from 'TemplateEditor/Layout/Blocks'
import {Config} from 'TemplateEditor/Layout/Blocks/Config'
import {Content} from 'TemplateEditor/Layout/Content'
import {ListSections} from 'TemplateEditor/Layout/Sections/Section'
import {SectionConfig} from 'TemplateEditor/Layout/Sections/SectionConfig'
import {SubSectionConfig} from 'TemplateEditor/Layout/Sections/SubSectionConfig'
import {Sidebar} from 'TemplateEditor/Layout/Sidebar'
import {useTemplate} from 'TemplateEditor/TemplateProvider'
import {
  applyDefaultPaddingToNewBlock,
  applyDefaultPaddingToNewSection,
} from 'TemplateEditor/defaultPaddingUtils'
import {Section, Template} from 'TemplateEditor/template'
import React from 'react'
import {DragDropContext, DropResult} from 'react-beautiful-dnd'
import styled from 'styled-components'

export function Layout() {
  const {template, updateTemplate} = useTemplate()
  const {editMode, mobileMode} = useEditor()

  return (
    <Container>
      <DragDropStretch>
        <DragDropContext
          onDragEnd={(result: DropResult) => {
            handleDragEnd(result, template, updateTemplate)
          }}
        >
          <Editor editMode={editMode}>
            <InnerContainer
              className="container"
              editMode={editMode}
              mobileMode={mobileMode}
            >
              <TemplateViewerScroll>
                <Content />
              </TemplateViewerScroll>
            </InnerContainer>

            <SideBarRender />
          </Editor>
        </DragDropContext>
      </DragDropStretch>
    </Container>
  )
}

function SideBarRender() {
  const {editMode, keepSidebar, editElement} = useEditor()

  const isSectionEdit = isSectionElement(editElement)

  if (isSectionEdit) return <SectionConfig key={editElement ?? 'section'} />

  const isSubSectionEdit = isSubSectionElement(editElement)

  if (isSubSectionEdit)
    return <SubSectionConfig key={editElement ?? 'subsection'} />

  if (editElement) return <Config key={editElement} />

  if (editMode || keepSidebar) return <Sidebar />

  return null
}

function isSectionElement(editElement: string | null) {
  // sections.${sectionIndex}
  if (!editElement) return false

  const keys = editElement.split('.')

  if (keys.length === 2 && keys[0] === 'sections') return true

  return false
}

function isSubSectionElement(editElement: string | null) {
  // sections.${sectionIndex}.sub.${index}
  if (!editElement) return false

  const keys = editElement.split('.')

  if (keys.length === 4 && keys[0] === 'sections' && keys[2] === 'innerStyles')
    return true

  return false
}

const handleDragEnd = (
  result: DropResult,
  template: Template,
  updateTemplate: (template: Template) => void,
) => {
  const {source, destination, draggableId} = result

  const sections = !template.sections
    ? (template.sections = [])
    : template.sections

  // new section init
  if (
    destination?.droppableId === 'editor-sections' &&
    source?.droppableId === 'default-sections'
  ) {
    for (const ListSection of ListSections) {
      if (draggableId === ListSection.typeId) {
        sections.splice(
          destination.index,
          0,
          applyDefaultPaddingToNewSection(ListSection.new(), template),
        )
      }
    }
  } else if (destination && source?.droppableId === 'default-blocks') {
    for (const ListBlock of ListBlocks) {
      if (draggableId === ListBlock.typeId) {
        const dest = destination.droppableId.split('.')

        const sectionKey = findSection(dest[0], sections)

        if (!sections[Number(sectionKey)].hasOwnProperty('blocks')) {
          sections[Number(sectionKey)].blocks = {}
        }

        if (
          !sections[Number(sectionKey)].blocks.hasOwnProperty(Number(dest[1]))
        ) {
          sections[Number(sectionKey)].blocks[Number(dest[1])] = []
        }

        sections[Number(sectionKey)].blocks[Number(dest[1])].splice(
          destination.index,
          0,
          applyDefaultPaddingToNewBlock(ListBlock.new(), template),
        )
      }
    }
  } else if (
    destination?.droppableId === 'editor-sections' &&
    source?.droppableId === 'editor-sections'
  ) {
    const [removed] = sections.splice(source.index, 1)
    sections.splice(destination.index, 0, removed)
  } else if (destination && source) {
    const dest = destination.droppableId.split('.')
    const sour = source.droppableId.split('.')

    const sectionKey = findSection(dest[0], sections)

    if (!sections[Number(sectionKey)].hasOwnProperty('blocks')) {
      sections[Number(sectionKey)].blocks = {}
    }

    if (!sections[Number(sectionKey)].blocks.hasOwnProperty(Number(dest[1]))) {
      sections[Number(sectionKey)].blocks[Number(dest[1])] = []
    }

    const sectionSourKey = findSection(sour[0], sections)

    if (!sections[Number(sectionSourKey)].hasOwnProperty('blocks')) {
      sections[Number(sectionSourKey)].blocks = {}
    }

    if (
      !sections[Number(sectionSourKey)].blocks.hasOwnProperty(Number(dest[1]))
    ) {
      sections[Number(sectionSourKey)].blocks[Number(dest[1])] = []
    }

    const [removed] = sections[Number(sectionSourKey)].blocks[
      Number(sour[1])
    ].splice(source.index, 1)
    sections[Number(sectionKey)].blocks[Number(dest[1])].splice(
      destination.index,
      0,
      removed,
    )
  }

  updateTemplate({
    ...template,
    sections: sections,
  })
}

function findSection(sectionId: string, sections: Section[]) {
  return Object.keys(sections).find(
    (key) => sections[Number(key)].id === sectionId,
  )
}

const Container = styled.div`
  height: 100%;
  min-height: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
`

const DragDropStretch = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`

/** Org template editor top chrome (~toolbar); attendee `/event/preview` has none. */
const EDITOR_LAYOUT_TOP_PX = 76

const InnerContainer = styled.div<{editMode: boolean; mobileMode: boolean}>`
  flex: 1;
  min-width: 0;
  min-height: 0;
  max-height: calc(
    100vh - ${(props) => (props.editMode ? EDITOR_LAYOUT_TOP_PX : 0)}px
  );
  height: ${(props) =>
    props.editMode ? `calc(100vh - ${EDITOR_LAYOUT_TOP_PX}px)` : '100vh'};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  container-type: inline-size;
  width: 100%;
  max-width: ${(props) => (props.mobileMode ? '390px' : 'inherit')};
  padding: 0;
`

const TemplateViewerScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior-y: contain;
`

const Editor = styled.div<{editMode: boolean}>`
  display: flex;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  min-width: 0;
  width: 100%;
  max-height: calc(
    100vh - ${(props) => (props.editMode ? EDITOR_LAYOUT_TOP_PX : 0)}px
  );
  transition: all 0.3s ease;
`
